import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTenantConfig } from '../contexts/TenantConfigContext';
import { useFeatureFlags, Module } from '../contexts/FeatureFlagContext';
import {
  Activity,
  BarChart3,
  Building2,
  ChevronDown,
  ClipboardList,
  CreditCard,
  FileText,
  Landmark,
  Layers,
  Receipt,
  RefreshCw,
  Sigma,
  ShieldCheck,
  Wallet,
} from 'lucide-react';

// Institutional Components
import CashManagementScreen from './CashManagementScreen';
import LoanSettlementQueueScreen from './credit/LoanSettlementQueueScreen';
import CorporateActionsScreen from './CorporateActionsScreen';
import OptionsLifecycleScreen from './tickets/OptionsLifecycleScreen';
import SwapsLifecycleScreen from './tickets/SwapsLifecycleScreen';
import RepoLifecycleScreen from './tickets/RepoLifecycleScreen';
import CustodianReconciliationScreen from './CustodianReconciliationScreen';
import PrivateAssetReconciliationScreen from './PrivateAssetReconciliationScreen';
import ReconciliationCenterScreen from './ReconciliationCenterScreen';
import RegulatoryReportingScreen from './RegulatoryReportingScreen';
import NavReportingScreen from './NavReportingScreen';
import DocumentProcessingScreen from './DocumentProcessingScreen';
import InsuranceReportingScreen from './InsuranceReportingScreen';

// Retail Components (Performance and Client Statements are in RetailAnalyticsHub)
import RetailCashDashboard from './retail/RetailCashDashboard';
import RetailCorporateActionsScreen from './retail/RetailCorporateActionsScreen';
import FeeBillingScreen from './retail/FeeBillingScreen';
import DocumentInboxScreen from './retail/DocumentInboxScreen';
import AlternativeAssetsScreen from './retail/AlternativeAssetsScreen';
import { AxiomButton, AxiomMetricCard, AxiomMetricGrid, AxiomStatusBadge } from './shared';
import {
  CollapsedRail,
  CollapsedRailItem,
  CollapsedRailSection,
  Eyebrow,
  HeroSubtitle,
  HeroTitle,
  HeroTop,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  RailChevron,
  RailItem,
  RailItemDescription,
  RailItemLabel,
  RailSection,
  RailSectionHeader,
  RailSectionLabel,
  RailSectionMeta,
  ViewPill,
  ViewSwitch,
  WorkspaceBody,
  WorkspaceHero,
  WorkspaceShell,
  WorkspaceMain,
  WorkspacePanel,
  WorkspaceRail,
  WorkspaceRailToggle,
} from './shared/Workspace';

// Tab types for institutional
type InstitutionalTabType = 'cash' | 'loan-settlement' | 'corporate-actions' | 'derivatives-lifecycle' | 'reconciliation' | 'regulatory' | 'nav' | 'document-processing' | 'insurance';
type ReconciliationSubTabType = 'center' | 'custodian' | 'private-assets';

// Tab types for retail (Performance and Client Statements are in Analytics Hub, not Middle Office)
type RetailTabType = 'cash' | 'dividends' | 'reconciliation' | 'billing' | 'regulatory' | 'document-inbox' | 'alternatives';
type MiddleOfficeView = 'pm' | 'risk' | 'ops';
type MiddleOfficeTone = 'blue' | 'cyan' | 'emerald' | 'amber' | 'red' | 'violet' | 'slate';
type MiddleOfficeSection = 'operations' | 'reporting';

type MiddleOfficeTab<T extends string> = {
  id: T;
  icon: React.ReactNode;
  label: string;
  badge: string;
  description: string;
  section: MiddleOfficeSection;
  tone: MiddleOfficeTone;
  module?: Module;
};

const MiddleOfficeManagementHub: React.FC = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isInstitutional } = useTenantConfig();
  const { isModuleEnabled } = useFeatureFlags();

  // Initialize active tab based on current path and tenant type
  const getInitialInstitutionalTab = (): InstitutionalTabType => {
    const path = location.pathname;
    const tabParam = new URLSearchParams(location.search).get('tab');

    // Check query param first (e.g. /middle-office?tab=document-processing)
    if (tabParam === 'document-processing') return 'document-processing';
    if (tabParam === 'reconciliation') return 'reconciliation';
    if (tabParam === 'regulatory') return 'regulatory';
    if (tabParam === 'corporate-actions') return 'corporate-actions';
    if (tabParam === 'derivatives-lifecycle') return 'derivatives-lifecycle';
    if (tabParam === 'nav') return 'nav';
    if (tabParam === 'cash') return 'cash';
    if (tabParam === 'insurance') return 'insurance';
    if (tabParam === 'loan-settlement') return 'loan-settlement';

    // Fall back to path-based detection
    if (path.includes('/nav-reporting')) return 'nav';
    if (path.includes('/reconciliation')) return 'reconciliation';
    if (path.includes('/regulatory')) return 'regulatory';
    if (path.includes('/corporate-actions')) return 'corporate-actions';
    if (path.includes('/document-processing')) return 'document-processing';
    if (path.includes('/cash-management')) return 'cash';
    return 'cash';
  };

  const getInitialRetailTab = (): RetailTabType => {
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');

    // Check query param first
    if (tabParam === 'document-inbox') return 'document-inbox';
    if (tabParam === 'document-processing') return 'document-inbox'; // retail equivalent
    if (tabParam === 'alternatives') return 'alternatives';
    if (tabParam === 'cash') return 'cash';
    if (tabParam === 'dividends') return 'dividends';
    if (tabParam === 'reconciliation') return 'reconciliation';
    if (tabParam === 'billing') return 'billing';
    if (tabParam === 'regulatory') return 'regulatory';

    // Fall back to path-based detection
    if (path.includes('/dividends')) return 'dividends';
    if (path.includes('/document-inbox')) return 'document-inbox';
    if (path.includes('/alternatives') || path.includes('/alternative-assets')) return 'alternatives';
    if (path.includes('/reconciliation')) return 'reconciliation';
    if (path.includes('/billing')) return 'billing';
    if (path.includes('/regulatory')) return 'regulatory';
    if (path.includes('/cash')) return 'cash';
    return 'cash';
  };

  const [activeInstitutionalTab, setActiveInstitutionalTab] = useState<InstitutionalTabType>(getInitialInstitutionalTab());
  const [activeRetailTab, setActiveRetailTab] = useState<RetailTabType>(getInitialRetailTab());
  const [reconciliationSubTab, setReconciliationSubTab] = useState<ReconciliationSubTabType>('center');
  const [workspaceView, setWorkspaceView] = useState<MiddleOfficeView>('ops');
  const [railOpen, setRailOpen] = useState({ operations: true, reporting: true });
  const [railCollapsed, setRailCollapsed] = useState(false);

  useEffect(() => {
    if (isInstitutional) {
      const tab = getInitialInstitutionalTab();
      if (tab !== activeInstitutionalTab) {
        setActiveInstitutionalTab(tab);
      }
    } else {
      const tab = getInitialRetailTab();
      if (tab !== activeRetailTab) {
        setActiveRetailTab(tab);
      }
    }
  }, [location.pathname, location.search, isInstitutional]);

  // Institutional tabs configuration — gated by per-tenant module licenses.
  // Cash management + Document processing + Regulatory are always on for MIDDLE_OFFICE
  // (Regulatory reporting screen handles its own internal toggles for FORM_PF/13F).
  const allInstitutionalTabs: MiddleOfficeTab<InstitutionalTabType>[] = [
    {
      id: 'cash',
      icon: <Wallet />,
      label: 'Liquidity Command',
      badge: 'Treasury',
      description: 'Cash balances, settlement liquidity, sweeps and funding pressure.',
      section: 'operations',
      tone: 'emerald',
    },
    {
      id: 'loan-settlement',
      icon: <RefreshCw />,
      label: 'Loan Settlement',
      badge: 'LSTA',
      description: 'Open-trade queue: T+7/T+20 aging, delayed compensation, legal transfer and settle.',
      section: 'operations',
      tone: 'amber',
    },
    {
      id: 'reconciliation',
      icon: <RefreshCw />,
      label: 'Reconciliation',
      badge: 'SWIFT',
      description: 'Custodian, SWIFT and private-asset break management.',
      section: 'operations',
      tone: 'cyan',
      module: Module.SWIFT_RECON,
    },
    {
      id: 'document-processing',
      icon: <FileText />,
      label: 'Document Processing',
      badge: 'AI',
      description: 'AI-assisted ingestion, extraction, review and booking controls.',
      section: 'operations',
      tone: 'blue',
    },
    {
      id: 'corporate-actions',
      icon: <ClipboardList />,
      label: 'Corporate Actions',
      badge: 'Events',
      description: 'Mandatory and voluntary events, elections and downstream booking.',
      section: 'operations',
      tone: 'amber',
      module: Module.CORPORATE_ACTIONS,
    },
    {
      id: 'derivatives-lifecycle',
      icon: <Sigma />,
      label: 'Derivatives Lifecycle',
      // F-FX-026: the badge and description still described this tab as
      // options-only, years after it grew to host the whole swaps panel
      // (IRS / CDS / CDX / TRS / CCS / Swaption / FX). An operator looking
      // for the FX swap near-leg settle had no way to find it from here.
      badge: 'Options & Swaps',
      description: 'Option marks, close-outs, exercise/assignment and expiry; swap and FX marks, near-leg settles, NDF fixings and unwinds.',
      section: 'operations',
      tone: 'violet',
    },
    {
      id: 'nav',
      icon: <BarChart3 />,
      label: 'NAV Reporting',
      badge: 'Daily',
      description: 'Daily NAV production, approvals and valuation reporting.',
      section: 'reporting',
      tone: 'violet',
      module: Module.NAV_CALCULATION,
    },
    {
      id: 'regulatory',
      icon: <ShieldCheck />,
      label: 'Regulatory',
      badge: 'SEC',
      description: 'Form PF, 13F, ADV and regulatory production controls.',
      section: 'reporting',
      tone: 'cyan',
    },
    {
      id: 'insurance',
      icon: <Landmark />,
      label: 'Insurance Reporting',
      badge: 'NAIC',
      description: 'NAIC, statutory schedules and insurer reporting workflows.',
      section: 'reporting',
      tone: 'blue',
      module: Module.INSURANCE_REPORTING,
    },
  ];
  const institutionalTabs = allInstitutionalTabs.filter(t => !t.module || isModuleEnabled(t.module));

  // If the URL-resolved active tab was filtered out (tenant lacks the module),
  // fall back to the first visible tab so we never render an empty pane.
  useEffect(() => {
    if (!isInstitutional) return;
    if (!institutionalTabs.some(t => t.id === activeInstitutionalTab)) {
      const fallback = institutionalTabs[0]?.id;
      if (fallback) setActiveInstitutionalTab(fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInstitutional, activeInstitutionalTab, institutionalTabs.length]);

  // Retail tabs configuration (Performance and Client Statements moved to Analytics Hub)
  const retailTabs: MiddleOfficeTab<RetailTabType>[] = [
    {
      id: 'cash',
      icon: <Wallet />,
      label: 'Cash Dashboard',
      badge: 'Sweep',
      description: 'Household and account cash, sweeps and liquidity exceptions.',
      section: 'operations',
      tone: 'emerald',
    },
    {
      id: 'dividends',
      icon: <Receipt />,
      label: 'Dividends & Events',
      badge: 'Auto',
      description: 'Income events, dividend activity and client-facing cash events.',
      section: 'operations',
      tone: 'emerald',
    },
    {
      id: 'document-inbox',
      icon: <FileText />,
      label: 'Document Inbox',
      badge: 'AI',
      description: 'Client document intake, review and import workflow.',
      section: 'operations',
      tone: 'blue',
    },
    {
      id: 'alternatives',
      icon: <Layers />,
      label: 'Alternatives',
      badge: 'PE/VC',
      description: 'Alternative asset documents, capital activity and valuation intake.',
      section: 'operations',
      tone: 'violet',
    },
    {
      id: 'reconciliation',
      icon: <RefreshCw />,
      label: 'Reconciliation',
      badge: 'Custodian',
      description: 'Custodian position and cash reconciliation.',
      section: 'reporting',
      tone: 'cyan',
    },
    {
      id: 'billing',
      icon: <CreditCard />,
      label: 'Fee Billing',
      badge: 'AUM',
      description: 'Fee schedules, billing runs and invoice controls.',
      section: 'reporting',
      tone: 'amber',
    },
    {
      id: 'regulatory',
      icon: <ShieldCheck />,
      label: 'Regulatory',
      badge: 'ADV',
      description: 'Retail advisory regulatory and filing workflows.',
      section: 'reporting',
      tone: 'cyan',
    },
  ];

  const viewConfig: Record<MiddleOfficeView, {
    label: string;
    defaultInstitutionalTab: InstitutionalTabType;
    defaultRetailTab: RetailTabType;
    copy: string;
    operationsOrder: string[];
    reportingOrder: string[];
  }> = {
    pm: {
      label: 'PM View',
      defaultInstitutionalTab: 'cash',
      defaultRetailTab: 'cash',
      copy: 'Prioritises liquidity, NAV readiness, document intake and operational items that affect portfolio decisions.',
      operationsOrder: ['cash', 'document-processing', 'reconciliation', 'corporate-actions', 'document-inbox', 'alternatives', 'dividends'],
      reportingOrder: ['nav', 'regulatory', 'insurance', 'billing'],
    },
    risk: {
      label: 'Risk View',
      defaultInstitutionalTab: 'regulatory',
      defaultRetailTab: 'reconciliation',
      copy: 'Prioritises controls, break escalation, regulatory production, audit evidence and unresolved exceptions.',
      operationsOrder: ['reconciliation', 'document-processing', 'corporate-actions', 'cash', 'document-inbox', 'alternatives', 'dividends'],
      reportingOrder: ['regulatory', 'insurance', 'nav', 'billing'],
    },
    ops: {
      label: 'Ops View',
      defaultInstitutionalTab: 'reconciliation',
      defaultRetailTab: 'document-inbox',
      copy: 'Prioritises daily production: reconcile, process documents, resolve events, produce NAV and close control breaks.',
      operationsOrder: ['reconciliation', 'document-processing', 'cash', 'corporate-actions', 'document-inbox', 'dividends', 'alternatives'],
      reportingOrder: ['nav', 'regulatory', 'insurance', 'billing'],
    },
  };

  const activeView = viewConfig[workspaceView];

  const syncTabParam = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const handleInstitutionalTabChange = (tab: InstitutionalTabType) => {
    setActiveInstitutionalTab(tab);
    syncTabParam(tab);
  };

  const handleRetailTabChange = (tab: RetailTabType) => {
    setActiveRetailTab(tab);
    syncTabParam(tab);
  };

  const orderTabs = <T extends string,>(tabs: MiddleOfficeTab<T>[], order: string[]) => {
    const byId = new Map(tabs.map(tab => [tab.id, tab]));
    const ordered = order.map(id => byId.get(id as T)).filter(Boolean) as MiddleOfficeTab<T>[];
    const remaining = tabs.filter(tab => !order.includes(tab.id));
    return [...ordered, ...remaining];
  };

  const renderRailSection = <T extends string,>(
    label: string,
    openKey: 'operations' | 'reporting',
    tabs: MiddleOfficeTab<T>[],
    activeTab: T,
    onTabChange: (tab: T) => void,
  ) => (
    <RailSection>
      <RailSectionHeader
        type="button"
        aria-expanded={railOpen[openKey]}
        onClick={() => setRailOpen(prev => ({ ...prev, [openKey]: !prev[openKey] }))}
      >
        <RailSectionLabel>{label}</RailSectionLabel>
        <RailSectionMeta>
          {tabs.length}
          <RailChevron $open={railOpen[openKey]}><ChevronDown /></RailChevron>
        </RailSectionMeta>
      </RailSectionHeader>
      <AnimatePresence initial={false}>
        {railOpen[openKey] && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.16 }}
            style={{ overflow: 'hidden' }}
          >
            {tabs.map(tab => (
              <RailItem
                key={tab.id}
                $active={activeTab === tab.id}
                $tone={tab.tone}
                onClick={() => onTabChange(tab.id)}
              >
                {tab.icon}
                <div>
                  <RailItemLabel>{tab.label}</RailItemLabel>
                  <RailItemDescription>{tab.description}</RailItemDescription>
                </div>
              </RailItem>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </RailSection>
  );

  const renderCollapsedRailSection = <T extends string,>(
    tabs: MiddleOfficeTab<T>[],
    activeTab: T,
    onTabChange: (tab: T) => void,
  ) => (
    <CollapsedRailSection>
      {tabs.map(tab => (
        <CollapsedRailItem
          key={tab.id}
          $active={activeTab === tab.id}
          $tone={tab.tone}
          aria-label={tab.label}
          title={`${tab.label} - ${tab.description}`}
          onClick={() => onTabChange(tab.id)}
        >
          {tab.icon}
        </CollapsedRailItem>
      ))}
    </CollapsedRailSection>
  );

  const renderReconciliation = () => (
    <>
      <div className="flex flex-wrap gap-2 border-b border-white/10 bg-slate-950/20 p-4">
        <AxiomButton
          type="button"
          onClick={() => setReconciliationSubTab('center')}
          $variant={reconciliationSubTab === 'center' ? 'primary' : 'secondary'}
        >
          Reconciliation Center <AxiomStatusBadge $tone="emerald">3-Way</AxiomStatusBadge>
        </AxiomButton>
        <AxiomButton
          type="button"
          onClick={() => setReconciliationSubTab('custodian')}
          $variant={reconciliationSubTab === 'custodian' ? 'primary' : 'secondary'}
        >
          SWIFT Reconciliation <AxiomStatusBadge $tone="cyan">MT535</AxiomStatusBadge>
        </AxiomButton>
        <AxiomButton
          type="button"
          onClick={() => setReconciliationSubTab('private-assets')}
          $variant={reconciliationSubTab === 'private-assets' ? 'primary' : 'secondary'}
        >
          Private Assets <AxiomStatusBadge $tone="violet">NAV</AxiomStatusBadge>
        </AxiomButton>
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={reconciliationSubTab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.15 }}
        >
          {reconciliationSubTab === 'center' && <ReconciliationCenterScreen />}
          {reconciliationSubTab === 'custodian' && <CustodianReconciliationScreen />}
          {reconciliationSubTab === 'private-assets' && <PrivateAssetReconciliationScreen />}
        </motion.div>
      </AnimatePresence>
    </>
  );

  const renderInstitutionalContent = () => {
    if (activeInstitutionalTab === 'cash') return <CashManagementScreen />;
    if (activeInstitutionalTab === 'loan-settlement') return <LoanSettlementQueueScreen />;
    if (activeInstitutionalTab === 'corporate-actions') return <CorporateActionsScreen />;
    if (activeInstitutionalTab === 'derivatives-lifecycle') return (
      <>
        <OptionsLifecycleScreen />
        <SwapsLifecycleScreen />
        <RepoLifecycleScreen />
      </>
    );
    if (activeInstitutionalTab === 'reconciliation') return renderReconciliation();
    if (activeInstitutionalTab === 'regulatory') return <RegulatoryReportingScreen />;
    if (activeInstitutionalTab === 'nav') return <NavReportingScreen />;
    if (activeInstitutionalTab === 'document-processing') return <DocumentProcessingScreen />;
    if (activeInstitutionalTab === 'insurance') return <InsuranceReportingScreen />;
    return <CashManagementScreen />;
  };

  const renderRetailContent = () => {
    if (activeRetailTab === 'cash') return <RetailCashDashboard />;
    if (activeRetailTab === 'dividends') return <RetailCorporateActionsScreen />;
    if (activeRetailTab === 'document-inbox') return <DocumentInboxScreen />;
    if (activeRetailTab === 'alternatives') {
      return <AlternativeAssetsScreen onNavigateToDocumentInbox={() => handleRetailTabChange('document-inbox')} />;
    }
    if (activeRetailTab === 'reconciliation') return <CustodianReconciliationScreen />;
    if (activeRetailTab === 'billing') return <FeeBillingScreen />;
    if (activeRetailTab === 'regulatory') return <RegulatoryReportingScreen />;
    return <RetailCashDashboard />;
  };

  const handleWorkspaceViewChange = (next: MiddleOfficeView) => {
    setWorkspaceView(next);
    const config = viewConfig[next];
    if (isInstitutional) {
      const fallback = institutionalTabs.some(tab => tab.id === config.defaultInstitutionalTab)
        ? config.defaultInstitutionalTab
        : institutionalTabs[0]?.id;
      if (fallback) handleInstitutionalTabChange(fallback);
    } else {
      handleRetailTabChange(config.defaultRetailTab);
    }
  };

  // Render Institutional Hub
  if (isInstitutional) {
    const operationsTabs = orderTabs(
      institutionalTabs.filter(tab => tab.section === 'operations'),
      activeView.operationsOrder,
    );
    const reportingTabs = orderTabs(
      institutionalTabs.filter(tab => tab.section === 'reporting'),
      activeView.reportingOrder,
    );
    const activeMeta = institutionalTabs.find(tab => tab.id === activeInstitutionalTab) || institutionalTabs[0];

    return (
      <WorkspaceShell>
        <WorkspaceHero>
          <HeroTop>
            <div>
              <Eyebrow $tone="cyan">Middle Office Workspace</Eyebrow>
              <HeroTitle>Middle Office Control Room</HeroTitle>
              <HeroSubtitle>
                One operating surface for liquidity, reconciliation, document ingestion, corporate actions,
                NAV production and regulatory reporting.
              </HeroSubtitle>
            </div>
            <ViewSwitch aria-label="Middle office workspace view">
              <ViewPill $active={workspaceView === 'pm'} onClick={() => handleWorkspaceViewChange('pm')}>PM View</ViewPill>
              <ViewPill $active={workspaceView === 'risk'} onClick={() => handleWorkspaceViewChange('risk')}>Risk View</ViewPill>
              <ViewPill $active={workspaceView === 'ops'} onClick={() => handleWorkspaceViewChange('ops')}>Ops View</ViewPill>
            </ViewSwitch>
          </HeroTop>
          <HeroSubtitle>{activeView.copy}</HeroSubtitle>
          <AxiomMetricGrid style={{ marginTop: '1rem' }}>
            <AxiomMetricCard label="Active Lens" value={activeView.label} subtext="PM, risk or operations content order" icon={<Activity />} tone="cyan" />
            <AxiomMetricCard label="Daily Workflows" value={operationsTabs.length} subtext="Cash, recon, documents and events" icon={<Layers />} tone="emerald" />
            <AxiomMetricCard label="Control Workflows" value={reportingTabs.length} subtext="NAV, regulatory and insurance reporting" icon={<ShieldCheck />} tone="violet" />
          </AxiomMetricGrid>
        </WorkspaceHero>

        <WorkspaceBody $railCollapsed={railCollapsed}>
          <WorkspaceRail $collapsed={railCollapsed}>
            <WorkspaceRailToggle collapsed={railCollapsed} onToggle={() => setRailCollapsed(prev => !prev)} />
            {railCollapsed ? (
              <CollapsedRail>
                {renderCollapsedRailSection(operationsTabs, activeInstitutionalTab, handleInstitutionalTabChange)}
                {renderCollapsedRailSection(reportingTabs, activeInstitutionalTab, handleInstitutionalTabChange)}
              </CollapsedRail>
            ) : (
              <>
                {renderRailSection('Daily Operations', 'operations', operationsTabs, activeInstitutionalTab, handleInstitutionalTabChange)}
                {renderRailSection('Control & Reporting', 'reporting', reportingTabs, activeInstitutionalTab, handleInstitutionalTabChange)}
              </>
            )}
          </WorkspaceRail>

          <WorkspaceMain>
            <WorkspacePanel>
              <PanelHeader>
                <div>
                  <PanelTitle>{activeMeta?.label || 'Middle Office'}</PanelTitle>
                  <PanelSubtitle>
                    {activeMeta?.description || 'Operational workflow'} · {activeView.label}
                  </PanelSubtitle>
                </div>
                {activeMeta?.badge && (
                  <AxiomStatusBadge $tone={activeMeta.tone}>
                    <Activity className="h-4 w-4 text-cyan-400" />
                    {activeMeta.badge}
                  </AxiomStatusBadge>
                )}
              </PanelHeader>
              <PanelBody>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeInstitutionalTab}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.15 }}
                  >
                    {renderInstitutionalContent()}
                  </motion.div>
                </AnimatePresence>
              </PanelBody>
            </WorkspacePanel>
          </WorkspaceMain>
        </WorkspaceBody>
      </WorkspaceShell>
    );
  }

  // Render Retail Hub
  const retailOperationsTabs = orderTabs(retailTabs.filter(tab => tab.section === 'operations'), activeView.operationsOrder);
  const retailReportingTabs = orderTabs(retailTabs.filter(tab => tab.section === 'reporting'), activeView.reportingOrder);
  const activeRetailMeta = retailTabs.find(tab => tab.id === activeRetailTab) || retailTabs[0];

  return (
    <WorkspaceShell>
      <WorkspaceHero>
        <HeroTop>
          <div>
            <Eyebrow $tone="emerald">Retail Middle Office</Eyebrow>
            <HeroTitle>Middle Office Control Room</HeroTitle>
            <HeroSubtitle>
              One operating surface for cash, dividends, document intake, alternatives, reconciliation,
              billing and advisory regulatory work.
            </HeroSubtitle>
          </div>
          <ViewSwitch aria-label="Retail middle office workspace view">
            <ViewPill $active={workspaceView === 'pm'} onClick={() => handleWorkspaceViewChange('pm')}>Advisor View</ViewPill>
            <ViewPill $active={workspaceView === 'risk'} onClick={() => handleWorkspaceViewChange('risk')}>Control View</ViewPill>
            <ViewPill $active={workspaceView === 'ops'} onClick={() => handleWorkspaceViewChange('ops')}>Ops View</ViewPill>
          </ViewSwitch>
        </HeroTop>
        <HeroSubtitle>{activeView.copy}</HeroSubtitle>
        <AxiomMetricGrid style={{ marginTop: '1rem' }}>
          <AxiomMetricCard label="Active Lens" value={activeView.label} subtext="Advisor, control or operations content order" icon={<Activity />} tone="emerald" />
          <AxiomMetricCard label="Daily Workflows" value={retailOperationsTabs.length} subtext="Cash, documents, dividends and alternatives" icon={<Layers />} tone="cyan" />
          <AxiomMetricCard label="Control Workflows" value={retailReportingTabs.length} subtext="Recon, billing and regulatory production" icon={<ShieldCheck />} tone="violet" />
        </AxiomMetricGrid>
      </WorkspaceHero>

      <WorkspaceBody $railCollapsed={railCollapsed}>
        <WorkspaceRail $collapsed={railCollapsed}>
          <WorkspaceRailToggle collapsed={railCollapsed} onToggle={() => setRailCollapsed(prev => !prev)} />
          {railCollapsed ? (
            <CollapsedRail>
              {renderCollapsedRailSection(retailOperationsTabs, activeRetailTab, handleRetailTabChange)}
              {renderCollapsedRailSection(retailReportingTabs, activeRetailTab, handleRetailTabChange)}
            </CollapsedRail>
          ) : (
            <>
              {renderRailSection('Daily Operations', 'operations', retailOperationsTabs, activeRetailTab, handleRetailTabChange)}
              {renderRailSection('Control & Reporting', 'reporting', retailReportingTabs, activeRetailTab, handleRetailTabChange)}
            </>
          )}
        </WorkspaceRail>

        <WorkspaceMain>
          <WorkspacePanel>
            <PanelHeader>
              <div>
                <PanelTitle>{activeRetailMeta.label}</PanelTitle>
                <PanelSubtitle>{activeRetailMeta.description} · {activeView.label}</PanelSubtitle>
              </div>
              <AxiomStatusBadge $tone={activeRetailMeta.tone}>
                <Building2 className="h-4 w-4 text-emerald-400" />
                {activeRetailMeta.badge}
              </AxiomStatusBadge>
            </PanelHeader>
            <PanelBody>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeRetailTab}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.15 }}
                >
                  {renderRetailContent()}
                </motion.div>
              </AnimatePresence>
            </PanelBody>
          </WorkspacePanel>
        </WorkspaceMain>
      </WorkspaceBody>
    </WorkspaceShell>
  );
};

export default MiddleOfficeManagementHub;
